import { z } from 'zod';

// ---------------------------------------------------------------------------
// Enums — mirror the API's CalendarEvent / EventReservation models.
// ---------------------------------------------------------------------------

export const EVENT_STATUSES = ['draft', 'published', 'open', 'cancelled', 'finalized'] as const;
export const EventStatusSchema = z.enum(EVENT_STATUSES);
export type EventStatus = z.infer<typeof EventStatusSchema>;

export const RESERVATION_STATUSES = [
    'pending',
    'confirmed',
    'cancelled',
    'checked_in',
    'no_show',
] as const;
export const ReservationStatusSchema = z.enum(RESERVATION_STATUSES);
export type ReservationStatus = z.infer<typeof ReservationStatusSchema>;

export const PAYMENT_STATUSES = ['unpaid', 'paid', 'refunded'] as const;
export const PaymentStatusSchema = z.enum(PAYMENT_STATUSES);
export type PaymentStatus = z.infer<typeof PaymentStatusSchema>;

export const LOCATIONS = ['KRIT Ploiesti', 'KRIT Brasov'] as const;
export const LocationSchema = z.enum(LOCATIONS);
export type EventLocation = z.infer<typeof LocationSchema>;

// Sequelize serializes DECIMAL columns as strings — accept both and
// normalize to number | null.
const decimal = z.preprocess(
    (v) => (v == null || v === '' ? null : Number(v)),
    z.number().nullable(),
);

// ---------------------------------------------------------------------------
// Highlights — display-only value/label chips on an event (e.g. KOINS
// scenarios: value "2", label "participare"). Rendered on the preview image,
// the storefront and the admin; zero/empty values are hidden everywhere.
// ---------------------------------------------------------------------------

export const EventHighlightSchema = z.object({
    key: z.string().nullish(),
    label: z.string(),
    value: z.union([z.string(), z.number()]).transform(String),
    icon: z.string().nullish(),
});
export type EventHighlight = z.infer<typeof EventHighlightSchema>;

// ---------------------------------------------------------------------------
// Reservations — the events list includes a trimmed attribute set, the event
// detail includes everything; optional fields cover both shapes.
// ---------------------------------------------------------------------------

export const ReservationSchema = z.object({
    id: z.number(),
    status: ReservationStatusSchema,
    paymentStatus: PaymentStatusSchema,
    confirmationCode: z.string(),
    name: z.string(),
    email: z.string(),
    phone: z.string(),
    amount: decimal.optional(),
    notes: z.string().nullish(),
    userId: z.number().nullish(),
    eventId: z.number().optional(),
    paymentTypeId: z.number().nullish(),
    paymentType: z.object({ id: z.number(), name: z.string() }).nullish(),
    createdAt: z.string().optional(),
});
export type Reservation = z.infer<typeof ReservationSchema>;

// Rows from the global `/event-reservations` list — same shape plus the
// included event summary.
export const ReservationListItemSchema = ReservationSchema.extend({
    eventId: z.number(),
    event: z
        .object({
            id: z.number(),
            title: z.string(),
            start: z.string(),
            end: z.string().optional(),
            location: z.string().optional(),
            price: decimal.optional(),
        })
        .nullish(),
});
export type ReservationListItem = z.infer<typeof ReservationListItemSchema>;

export const ReservationListSchema = z.object({
    data: z.array(ReservationListItemSchema),
    count: z.number().optional(),
});

// ---------------------------------------------------------------------------
// Consumables — `/events/:id/consumables` hydrates `.item`; the event detail
// includes `product` / `single` instead. The api layer normalizes both into
// `item` before parsing.
// ---------------------------------------------------------------------------

export const ConsumableItemSchema = z
    .object({
        id: z.number(),
        // Product fields
        title: z.string().nullish(),
        sku: z.string().nullish(),
        thumbnail: z.string().nullish(),
        // Card fields
        name: z.string().nullish(),
        number: z.union([z.string(), z.number()]).nullish(),
        image: z.string().nullish(),
        expansion: z.object({ name: z.string().nullish() }).passthrough().nullish(),
    })
    .passthrough();
export type ConsumableItem = z.infer<typeof ConsumableItemSchema>;

export const ConsumableSchema = z.object({
    id: z.number().optional(),
    eventId: z.number().optional(),
    itemType: z.enum(['product', 'single']),
    itemId: z.number(),
    quantity: z.number(),
    item: ConsumableItemSchema.nullish(),
});
export type Consumable = z.infer<typeof ConsumableSchema>;

export const ConsumableListSchema = z.object({ data: z.array(ConsumableSchema) });

// ---------------------------------------------------------------------------
// Rounds — tournament round slots set from the "Evenimente curente" board,
// stored in the event's generic `meta` JSON bucket (meta.rounds). Times are
// 'HH:MM' (24h) or '' while being filled in.
// ---------------------------------------------------------------------------

export const EventRoundSchema = z.object({
    start: z.string(),
    end: z.string(),
    // Break length (minutes) after this round; absent on rounds saved before breaks.
    breakAfter: z.number().int().nonnegative().nullish(),
});
export type EventRound = z.infer<typeof EventRoundSchema>;

export const EventMetaSchema = z
    .object({
        rounds: z.array(EventRoundSchema).nullish(),
        // Per-event defaults used to seed new rounds/breaks on the board.
        defaultRound: z.number().int().positive().nullish(),
        defaultBreak: z.number().int().nonnegative().nullish(),
        // Round start/end alert pack (1-3) assigned to this event.
        audioVersion: z.number().int().min(1).max(3).nullish(),
    })
    .passthrough();
export type EventMeta = z.infer<typeof EventMetaSchema>;

// ---------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------

export const CalendarEventSchema = z.object({
    id: z.number(),
    title: z.string(),
    description: z.string().nullish(),
    rules: z.string().nullish(),
    prizes: z.string().nullish(),
    start: z.string(),
    end: z.string(),
    type: z.string(),
    subtype: z.string().nullish(),
    capacity: z.number().nullish(),
    location: z.string(),
    price: decimal.optional(),
    imageLogo: z.string().nullish(),
    imageCalendarBackground: z.string().nullish(),
    imageWideBackground: z.string().nullish(),
    // Defensive: MariaDB JSON columns can leak through as raw strings.
    highlights: z.preprocess(
        (v) => {
            if (typeof v !== 'string') return v;
            try {
                return JSON.parse(v);
            } catch {
                return null;
            }
        },
        z.array(EventHighlightSchema).nullish(),
    ),
    // Defensive: MariaDB JSON columns can leak through as raw strings.
    meta: z.preprocess(
        (v) => {
            if (typeof v !== 'string') return v;
            try {
                return JSON.parse(v);
            } catch {
                return null;
            }
        },
        EventMetaSchema.nullish(),
    ),
    status: EventStatusSchema,
    slug: z.string().optional(),
    platformEventId: z.string().nullish(),
    stockOperationId: z.number().nullish(),
    revision: z.number().optional(),
    reservations: z.array(ReservationSchema).optional(),
    consumables: z.array(ConsumableSchema).optional(),
});
export type CalendarEvent = z.infer<typeof CalendarEventSchema>;

export const EventListSchema = z.object({
    data: z.array(CalendarEventSchema),
    count: z.number().optional(),
});
export const EventResponseSchema = z.object({ data: CalendarEventSchema });

export const PublishMonthResultSchema = z.object({
    data: z.object({ published: z.number(), failed: z.number() }),
});

export const PaymentTypeListSchema = z.object({
    data: z.array(z.object({ id: z.number(), name: z.string() }).passthrough()),
});
export type PaymentTypeOption = z.infer<typeof PaymentTypeListSchema>['data'][number];

// ---------------------------------------------------------------------------
// Event form (RHF) — numeric fields stay strings in the form and are
// converted in the submit payload builder.
// ---------------------------------------------------------------------------

export const EventFormSchema = z
    .object({
        title: z.string().trim().min(1, 'Titlul este obligatoriu'),
        type: z.string().min(1, 'Alege tipul evenimentului'),
        subtype: z.string().optional().or(z.literal('')),
        location: LocationSchema,
        start: z.string().min(1, 'Alege data si ora de inceput'),
        hasEndTime: z.boolean(),
        end: z.string().optional().or(z.literal('')),
        capacity: z
            .string()
            .regex(/^\d*$/, 'Numar intreg')
            .optional()
            .or(z.literal('')),
        price: z
            .string()
            .regex(/^\d*([.,]\d{0,2})?$/, 'Pret invalid')
            .optional()
            .or(z.literal('')),
        platformEventId: z.string().optional().or(z.literal('')),
        description: z.string().optional().or(z.literal('')),
        rules: z.string().optional().or(z.literal('')),
        prizes: z.string().optional().or(z.literal('')),
        imageLogo: z.string().nullable(),
        imageCalendarBackground: z.string().nullable(),
        imageWideBackground: z.string().nullable(),
        highlights: z
            .array(
                z.object({
                    key: z.string().optional().or(z.literal('')),
                    icon: z.string().nullable(),
                    value: z
                        .string()
                        .trim()
                        .min(1, 'Valoarea este obligatorie')
                        .max(40, 'Maxim 40 caractere'),
                    label: z
                        .string()
                        .trim()
                        .min(1, 'Eticheta este obligatorie')
                        .max(40, 'Maxim 40 caractere'),
                }),
            )
            .max(4, 'Maxim 4 proprietati'),
    })
    .superRefine((values, ctx) => {
        if (values.hasEndTime) {
            if (!values.end) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['end'],
                    message: 'Alege ora de sfarsit',
                });
            } else if (values.start && new Date(values.end) <= new Date(values.start)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['end'],
                    message: 'Sfarsitul trebuie sa fie dupa inceput',
                });
            }
        }
    });
export type EventForm = z.infer<typeof EventFormSchema>;

// What the create/update endpoints accept.
export type EventPayload = {
    title: string;
    description: string;
    rules: string;
    prizes: string;
    start: string;
    end: string;
    type: string;
    subtype: string;
    capacity: number | null;
    location: EventLocation;
    price: number | null;
    imageLogo: string | null;
    imageCalendarBackground: string | null;
    imageWideBackground: string | null;
    platformEventId: string | null;
    highlights: Array<{ key: string; label: string; value: string; icon: string | null }>;
};

// ---------------------------------------------------------------------------
// Bulk import/export — the admin parses the CSV client-side into rows, posts
// them to /events/bulk-import (dryRun first for the simulation, then for real),
// and renders the returned report. Row strings are validated server-side.
// ---------------------------------------------------------------------------

export type BulkImportRow = {
    _row: number; // 1-based CSV line (incl. header) for the report
    id: string;
    title: string;
    type: string;
    subtype: string;
    location: string;
    start: string; // ISO (client converts the local CSV datetime)
    end: string; // ISO or ''
    capacity: string;
    price: string;
    platformEventId: string;
    description: string;
    rules: string;
    prizes: string;
    highlights: string; // raw JSON cell, parsed server-side
    images: { imageLogo: string; imageCalendarBackground: string; imageWideBackground: string };
    replace: { imageLogo: boolean; imageCalendarBackground: boolean; imageWideBackground: boolean };
};

export type BulkImportRequest = {
    dryRun: boolean;
    events: BulkImportRow[];
    delete?: { enabled: boolean; startDate: string; endDate: string; location: EventLocation };
};

export const BulkImportReportSchema = z.object({
    dryRun: z.boolean(),
    summary: z.object({
        created: z.number(),
        updated: z.number(),
        skipped: z.number(),
        failed: z.number(),
        deleted: z.number(),
    }),
    rows: z.array(
        z.object({
            row: z.number().nullable(),
            id: z.number().nullable(),
            title: z.string(),
            action: z.enum(['create', 'update', 'skip', 'error']),
            message: z.string().optional(),
        }),
    ),
    deletions: z.array(
        z.object({
            id: z.number(),
            title: z.string(),
            start: z.string(),
            status: z.enum(['pending', 'deleted', 'skip', 'error']),
            message: z.string().optional(),
        }),
    ),
});
export type BulkImportReport = z.infer<typeof BulkImportReportSchema>;
export type BulkImportRowResult = BulkImportReport['rows'][number];
export type BulkImportDeletion = BulkImportReport['deletions'][number];

export const ReservationFormSchema = z.object({
    name: z.string().trim().min(1, 'Numele este obligatoriu'),
    email: z.string().trim().email('Email invalid'),
    phone: z.string().trim().min(1, 'Telefonul este obligatoriu'),
    paymentTypeId: z.string().optional().or(z.literal('')),
    amount: z
        .string()
        .regex(/^\d*([.,]\d{0,2})?$/, 'Suma invalida')
        .optional()
        .or(z.literal('')),
    notes: z.string().optional().or(z.literal('')),
});
export type ReservationForm = z.infer<typeof ReservationFormSchema>;
